/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : SelectEEDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useEffect, useState } from "react";
import { Box, Radio } from "@mui/material";
import SelectZone from "./SelectZone";
import CommonBreadcrumbs from "../../../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import SelectComponentList from "./SelectComponentList";
import { labCar } from "../../../../redux/actions";
import { useSelector } from "react-redux";
import {
  checkedIconStyle,
  unCheckedIconStyle,
} from "../../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";

function SelectEEDialog({
  isZonalProject,
  zoneList = [],
  selectedZone,
  setSelectedZone,
  selectedZoneName,
  setSelectedZoneName,
  showZone,
  setShowZone,
  selectedEE,
  setSelectedEE,
  disabledEEComponents = [],
}) {
  const zone = "zone";
  const ee = "ee";

  const [eeComponentModifiedList, setEEComponentModifiedList] = useState([]);
  const [eeCount, setEECount] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [projectID, setProjectID] = useState(null);
  const [variantID, setVariantID] = useState(null);

  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails,
  );
  const selectedSingleVariantDetails = useSelector(
    (state) => state.projectConfigReducer.selectedVariantDetails,
  );
  useEffect(() => {
    if (selectedProjectDetails?.project_id) {
      setProjectID(selectedProjectDetails?.project_id);
    }
    if (selectedSingleVariantDetails?.variant_id) {
      setVariantID(selectedSingleVariantDetails?.variant_id);
    }
  }, [selectedProjectDetails, selectedSingleVariantDetails]);

  const modifyEEData = (data) => {
    const modifiedList = data.map((item) => {
      return {
        data: item,
        id: item.ee_id,
        cardDataArray: [
          {
            key: "Number",
            value: item.assembly_number,
            keyStyle: { fontWeight: 600 },
            valueStyle: { fontWeight: 600 },
          },
          {
            key: "Description",
            value: item.description,
          },
          {
            key: "Version",
            value: item.version,
          },
        ],
      };
    });
    setEEComponentModifiedList(modifiedList);
  };

  const getChangedZone = (zone, zoneName) => {
    setSelectedZone(zone);
    setSelectedZoneName(zoneName);
    setShowZone(ee);
  };

  return (
    <Box>
      {showZone === zone && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              items={[
                {
                  label: "Select Zone",
                },
              ]}
            />
          </Box>
          <SelectZone
            zoneList={zoneList}
            getChangedZone={getChangedZone}
            selectedZone={selectedZone}
          />
        </Box>
      )}
      {showZone === ee && (
        <Box>
          <Box sx={{ mb: 1 }}>
            <CommonBreadcrumbs
              color={"black"}
              items={[
                ...(isZonalProject
                  ? [
                      {
                        label: selectedZoneName,
                        onClick: () => {
                          setShowZone(zone);
                        },
                      },
                    ]
                  : []),
                {
                  label: "EE Component",
                },
              ]}
            />
          </Box>
          <Box>
            <Box>
              <SelectComponentList
                dataList={eeComponentModifiedList}
                apiAction={labCar.get_ee_components_new}
                searchKey={"assembly_number"}
                placeholder={"Search EE"}
                page={page}
                rowsPerPage={rowsPerPage}
                handlePageChange={(page) => setPage(page)}
                totalCount={eeCount}
                enableApiRequest={projectID && variantID}
                isItemDisabled={(item) =>
                  disabledEEComponents.includes(Number(item?.data?.ee_id))
                }
                renderSelectIcon={(data) => {
                  const isDisabled = disabledEEComponents.includes(
                    Number(data.ee_id),
                  );
                  return (
                    <Box
                      sx={{ pl: 1.5, pt: 2 }}
                      // onClick={(e) => e.stopPropagation()} // prevent card click
                    >
                      <Radio
                        name="eeComponent"
                        value={data.ee_id}
                        checked={selectedEE?.ee_id === data.ee_id}
                        disabled={isDisabled}
                        onChange={(e) => {
                          e.stopPropagation();
                          if (!isDisabled) {
                            setSelectedEE(data);
                          }
                        }}
                        icon={unCheckedIconStyle({ disabled: isDisabled })}
                        checkedIcon={checkedIconStyle({ disabled: isDisabled })}
                        sx={{
                          p: 0,
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                      />
                      {/* <CustomRadioButtonWithoutForm
                        value={data.ee_id}
                        name={"ee"}
                        isChecked={selectedEE?.ee_id === data.ee_id}
                        onChange={(e) => {
                          e.stopPropagation();
                          setSelectedEE(data); // ONLY onChange
                        }}
                      /> */}
                    </Box>
                  );
                }}
                renderCardActionIcons={(data) => {
                  return (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        pt: 2,
                        pr: 2,
                      }}
                    >
                      {/* <ChevronRightIcon /> */}
                    </Box>
                  );
                }}
                getDataOnSearch={(data, resp) => {
                  if (resp.error) {
                    modifyEEData([]);
                  } else {
                    modifyEEData(resp.data);
                    setEECount(resp.total_count);
                  }
                }}
                params={{
                  ...(projectID && variantID
                    ? {
                        project_id: Number(projectID),
                        variant_id: Number(variantID),
                        ...(isZonalProject && { zone_position: selectedZone }),
                        status: "true",
                        page_number: page,
                        page_size: rowsPerPage,
                      }
                    : {}),
                }}
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default SelectEEDialog;
